/**
 * PushToCtmsButton — CTMS (CRIO) push button + status chip (Expansion Phase H).
 *
 * Renders in the ContactDetail identity header. Feature-flagged server-side:
 * GET /contacts/:id/ctms answers 503 ctms_disabled when CRM_CTMS_ENABLED is
 * off, and this component renders NOTHING (the whole feature stays invisible).
 *
 * Chip = the crm.ctms_sync pointer (none/queued/synced/failed + relTime). The
 * confirm dialog picks the study when the contact has attempts on several
 * (latest attempt's study preselected); with no attempts the push is
 * deliberately unavailable — the payload is the screening journey, so the
 * recruiter screens first. Types are local on purpose: types.ts was being
 * reworked by the Part 6 build in parallel (fold into types.ts later if ever
 * useful elsewhere).
 *
 * Clones DisqualifyModal's modal shell (Z.modalRaised) and its in-flight exit
 * lock: every dismiss path is disabled while the POST runs.
 */
import { useEffect, useState } from 'react';
import { CheckCircle2, Loader2, UploadCloud } from 'lucide-react';
import api from '../../services/api';
import { prettySlug, relTime } from '../lib';
import { Alert, Z } from './ui';

interface CtmsSync {
  id: string;
  system: string;
  status: 'queued' | 'synced' | 'failed';
  external_id: string | null;
  error: string | null;
  metadata: { simulated?: boolean; study_id?: string; study_slug?: string | null; study_title?: string | null };
  created_at: string | null;
  updated_at: string | null;
}

interface StudyOption {
  id: string;
  label: string;
}

export default function PushToCtmsButton({ contactId }: { contactId: string }) {
  // null = probing; false = feature off (render nothing); true = live.
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [sync, setSync] = useState<CtmsSync | null>(null);

  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<StudyOption[] | null>(null);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [studyId, setStudyId] = useState<string>('');
  const [pushing, setPushing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 'failed' is deliberately NOT a done state: an adapter failure keeps the
  // Push button visible so the recruiter can retry in place (a done state
  // would strand them with a study select and no submit — review round 1).
  // 'superseded' = a concurrent push took over mid-flight (review round 2):
  // neutral note, never a green success banner.
  const [done, setDone] = useState<'synced' | 'already' | 'superseded' | null>(null);

  useEffect(() => {
    let alive = true;
    setEnabled(null);
    setSync(null);
    api.get(`/api/crm/contacts/${contactId}/ctms`)
      .then((res) => {
        if (!alive) return;
        setSync(res.data?.sync ?? null);
        setEnabled(true);
      })
      .catch((e: any) => {
        if (!alive) return;
        // 503 ctms_disabled = feature off → invisible. Any other failure
        // keeps the button (the push itself will surface the real error).
        setEnabled(e?.response?.status === 503 ? false : true);
      });
    return () => { alive = false; };
  }, [contactId]);

  if (!enabled) return null;

  const openDialog = () => {
    setOpen(true);
    setError(null);
    setDone(null);
    setOptions(null);
    setOptionsError(null);
    api.get(`/api/crm/contacts/${contactId}/attempts`)
      .then((res) => {
        // getHistory() carries the study name on each attempt (study_title =
        // internal alias with public-title fallback; study_slug as a last
        // resort). There is NO top-level `studies` array — reading one left
        // every option labelled the literal 'Study'.
        const attempts: { study_id: string; study_title?: string | null; study_slug?: string | null }[] =
          res.data?.attempts || [];
        // Attempts arrive started_at DESC — first appearance = latest journey.
        const seen = new Set<string>();
        const opts: StudyOption[] = [];
        for (const a of attempts) {
          if (!a.study_id || seen.has(a.study_id)) continue;
          seen.add(a.study_id);
          const label = a.study_title || (a.study_slug ? prettySlug(a.study_slug) : 'Study');
          opts.push({ id: a.study_id, label });
        }
        setOptions(opts);
        setStudyId(opts[0]?.id || '');
      })
      .catch((e: any) => {
        const d = e?.response?.data || {};
        setOptions([]);
        setOptionsError(d.detail || d.error || 'Failed to load this contact’s studies.');
      });
  };

  const dismiss = () => {
    if (pushing) return; // in-flight lock — no exit path while the POST runs
    setOpen(false);
  };

  const push = async () => {
    if (!studyId || pushing) return;
    setPushing(true);
    setError(null);
    try {
      const res = await api.post(`/api/crm/contacts/${contactId}/ctms/push`, { study_id: studyId });
      const next: CtmsSync | null = res.data?.sync ?? null;
      setSync(next);
      if (res.data?.already_synced) setDone('already');
      else if (next?.status === 'failed') setError(next.error || 'The CTMS adapter reported a failure. You can retry.');
      else if (res.data?.superseded || next?.status !== 'synced') setDone('superseded');
      else setDone('synced');
    } catch (e: any) {
      if (e?.response?.status === 503) {
        // Flag flipped off mid-session — hide the feature entirely.
        setEnabled(false);
        setOpen(false);
      } else {
        const d = e?.response?.data || {};
        setError(d.detail || d.error || e.message || 'Push failed');
      }
    } finally {
      setPushing(false);
    }
  };

  const chip = sync && (
    sync.status === 'synced' ? (
      <span
        title={`Pushed to CRIO ${sync.metadata?.study_title ? `for ${sync.metadata.study_title} ` : ''}${relTime(sync.updated_at)}${sync.metadata?.simulated ? ' (simulated — CRIO credentials not connected yet)' : ''}`}
        className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/20 dark:text-green-400"
      >
        <CheckCircle2 className="h-3 w-3" /> CRIO · {relTime(sync.updated_at)}
      </span>
    ) : sync.status === 'queued' ? (
      <span
        title="Queued for the CTMS push"
        className="inline-flex items-center rounded-full bg-yellow-50 px-2.5 py-0.5 text-xs font-medium text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400"
      >
        CRIO · queued
      </span>
    ) : (
      <span
        title={sync.error ? `CTMS push failed: ${sync.error}` : 'CTMS push failed'}
        className="inline-flex items-center rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/20 dark:text-red-400"
      >
        CRIO failed
      </span>
    )
  );

  return (
    <>
      {chip}
      <button
        onClick={openDialog}
        aria-label="Push this contact to the CTMS"
        title="Send this contact's prescreening journey to CRIO"
        className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
      >
        <UploadCloud className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">Push to CTMS</span>
      </button>

      {open && (
        <div
          className={`fixed inset-0 ${Z.modalRaised} flex items-start justify-center overflow-y-auto bg-black/50 p-0 sm:p-4`}
          onClick={dismiss}
        >
          <div
            className="mt-auto w-full max-w-md rounded-t-xl bg-white p-5 shadow-xl sm:mb-auto dark:bg-gray-800 sm:rounded-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Push to CRIO"
          >
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Push to CRIO</h3>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              Sends the contact, the selected study&apos;s latest prescreening journey (answers included), and the
              medical profile. Re-pushing with no changes is a no-op.
            </p>

            <div className="mt-4 space-y-3">
              {options === null ? (
                <div className="flex items-center gap-2 py-2 text-sm text-gray-500 dark:text-gray-400">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading studies…
                </div>
              ) : options.length === 0 ? (
                <p className="rounded-lg bg-gray-50 p-3 text-sm text-gray-600 dark:bg-gray-700/50 dark:text-gray-300">
                  {optionsError || 'No prescreening attempts yet. Run a prescreen first — the CTMS push sends the prescreening journey for a study.'}
                </p>
              ) : (
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400" htmlFor="ctms-study">
                    Study
                  </label>
                  <select
                    id="ctms-study"
                    value={studyId}
                    onChange={(e) => setStudyId(e.target.value)}
                    disabled={pushing || done !== null}
                    className="min-h-[44px] w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 sm:min-h-0"
                  >
                    {options.map((o) => (
                      <option key={o.id} value={o.id}>{o.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {done === 'synced' && (
                <p className="rounded-lg bg-green-50 p-3 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-400">
                  Pushed to CRIO{sync?.metadata?.simulated ? ' (simulated — CRIO credentials are not connected yet, so nothing left the platform)' : ''}.
                </p>
              )}
              {done === 'already' && (
                <p className="rounded-lg bg-gray-50 p-3 text-sm text-gray-600 dark:bg-gray-700/50 dark:text-gray-300">
                  Already up to date — nothing has changed since the last push.
                </p>
              )}
              {done === 'superseded' && (
                <p className="rounded-lg bg-gray-50 p-3 text-sm text-gray-600 dark:bg-gray-700/50 dark:text-gray-300">
                  Another push for this contact ran at the same time and owns the result — the status chip shows
                  where it stands.
                </p>
              )}
              {error && done !== 'synced' && (
                <Alert tone="error" icon={false}>{error}</Alert>
              )}
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={dismiss}
                disabled={pushing}
                className="inline-flex min-h-[44px] items-center rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
              >
                {done ? 'Close' : 'Cancel'}
              </button>
              {!done && (
                <button
                  onClick={push}
                  disabled={pushing || !studyId || options === null || options.length === 0}
                  className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:focus:ring-offset-gray-900 sm:min-h-0"
                >
                  {pushing ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                  {pushing ? 'Pushing…' : 'Push to CRIO'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
